import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import * as React from 'react';

import { cn } from '@ui/lib/utils';

const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let colour = '#';
  for (let i = 0; i < 3; i += 1) {
    // eslint-disable-next-line no-bitwise
    const value = (hash >> (i * 8)) & 0xff;
    colour += `00${value.toString(16)}`.slice(-2);
  }
  return colour;
};

const getFirstLettersUppercase = (str: string) => {
  const strippedString = str.replace(/\(.*?\)|["\\-]/g, '').trim();
  return strippedString
    .split(' ')
    .slice(0, 3)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

const FILLED_SATURATION = 65;
const FILLED_MUTED_LIGHTNESS = 82;
const FILLED_VIVID_LIGHTNESS = 55;
const FILLED_MUTED_FOREGROUND_LIGHTNESS = 25;
const FILLED_VIVID_FOREGROUND = 'white';

const stringToHue = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
};

const getFilledColor = (name: string, muted: boolean) => {
  const lightness = muted ? FILLED_MUTED_LIGHTNESS : FILLED_VIVID_LIGHTNESS;
  return `hsl(${stringToHue(name)}, ${FILLED_SATURATION}%, ${lightness}%)`;
};

const getFilledForegroundColor = (name: string, muted: boolean) => {
  if (!muted) {
    return FILLED_VIVID_FOREGROUND;
  }
  return `hsl(${stringToHue(name)}, ${FILLED_SATURATION}%, ${FILLED_MUTED_FOREGROUND_LIGHTNESS}%)`;
};

const SQUARE_CLASSNAME = [
  'rounded-lg after:rounded-lg',
  '[&_[data-slot=avatar-image]]:rounded-lg [&_[data-slot=avatar-fallback]]:rounded-lg',
].join(' ');

interface InitialsAvatarProps
  extends Omit<
    AvatarPrimitive.Root.Props & { size?: 'default' | 'sm' | 'lg'; filled?: boolean; muted?: boolean },
    'children'
  > {
  name: string;
  seed?: string;
  image?: string | null;
  square?: boolean;
  solid?: boolean;
}

const InitialsAvatar = ({
  name,
  seed,
  image,
  square = false,
  filled = false,
  muted = false,
  solid = false,
  className,
  ...props
}: InitialsAvatarProps) => {
  const colorSeed = seed ?? name;
  const picture = !isNil(image) && !isEmpty(image) ? <AvatarImage src={image} alt="" /> : null;

  if (solid) {
    return (
      <Avatar
        className={cn('after:border-none', square && SQUARE_CLASSNAME, className)}
        style={{ backgroundColor: stringToColor(colorSeed) }}
        {...props}
      >
        {picture}
        <AvatarFallback className="bg-transparent font-bold text-white">
          {getFirstLettersUppercase(name)}
        </AvatarFallback>
      </Avatar>
    );
  }
  if (filled) {
    return (
      <Avatar
        className={cn('after:border-none', square && SQUARE_CLASSNAME, className)}
        style={{ backgroundColor: getFilledColor(colorSeed, muted) }}
        {...props}
      >
        {picture}
        <AvatarFallback
          className="bg-transparent font-bold"
          style={{ color: getFilledForegroundColor(colorSeed, muted) }}
        >
          {getFirstLettersUppercase(name)}
        </AvatarFallback>
      </Avatar>
    );
  }
  const color = stringToColor(colorSeed);
  return (
    <Avatar
      className={cn('after:border-none', square && SQUARE_CLASSNAME, className)}
      style={{ border: `1.5px solid ${color}` }}
      {...props}
    >
      {picture}
      <AvatarFallback className="bg-white font-bold" style={{ color }}>
        {getFirstLettersUppercase(name)}
      </AvatarFallback>
    </Avatar>
  );
};

const Avatar = ({
  className,
  size = 'default',
  ...props
}: AvatarPrimitive.Root.Props & {
  size?: 'default' | 'sm' | 'lg';
}) => {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      className={cn(
        'group/avatar relative flex size-8 shrink-0 rounded-full select-none',
        'after:absolute after:inset-0 after:rounded-full after:border after:border-border after:mix-blend-darken',
        'data-[size=lg]:size-10 data-[size=sm]:size-6 dark:after:mix-blend-lighten',
        className
      )}
      {...props}
    />
  );
};

const AvatarImage = ({ className, ...props }: AvatarPrimitive.Image.Props) => {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full rounded-full object-cover', className)}
      {...props}
    />
  );
};

const AvatarFallback = ({ className, ...props }: AvatarPrimitive.Fallback.Props) => {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'flex size-full items-center justify-center rounded-full bg-muted text-sm text-muted-foreground',
        'group-data-[size=sm]/avatar:text-xs',
        className
      )}
      {...props}
    />
  );
};

const AvatarBadge = ({ className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        'absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full select-none',
        'bg-primary text-primary-foreground bg-blend-color ring-2 ring-background',
        'group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden',
        'group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2',
        'group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2',
        className
      )}
      {...props}
    />
  );
};

const AvatarGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        'group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background',
        className
      )}
      {...props}
    />
  );
};

const AvatarGroupCount = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="avatar-group-count"
        className={cn(
          'relative flex size-8 shrink-0 items-center justify-center rounded-full',
          'bg-muted text-sm text-muted-foreground ring-2 ring-background',
          'group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6',
          '[&>svg]:size-4',
          'group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3',
          className
        )}
        {...props}
      />
    );
  }
);
AvatarGroupCount.displayName = 'AvatarGroupCount';

export { Avatar, AvatarBadge, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage, InitialsAvatar };
